import React, { useRef, useState } from 'react';
import { useStepperContext } from '@/context/stepper-provider';
import { Button } from "./ui/button";
import { Loader2, MoveLeft, MoveRight, Upload } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useValidatePalletConfigQuery } from "@/redux/slices/define/defineApiSlice";
import { usePushPdfTo3DxMutation } from "@/redux/slices/summary/summary-api-slice";
import { showErrorToast, showSuccessToast } from "./toast/toast";
import FieldNames from './pallet-config/FieldNames';
// Dynamically import the DownloadButton with no SSR
import DownloadButton, { type DownloadButtonHandle } from './pdf/DownloadButton';

interface FooterProps {
  onSubmit: () => Promise<void>;
  layout?: boolean;
  isLoading?: boolean;
  hasErrors?: boolean;
  hasUnsavedChanges?: boolean;
  noNext?: boolean;
  reportDisabled?: boolean;
  showPushTo3DX?: boolean;
  pushTo3DXDisabled?: boolean;
  dtrId?: string;
}

const Footer: React.FC<FooterProps> = ({
  onSubmit,
  layout,
  hasErrors,
  hasUnsavedChanges,
  noNext = false,
  isLoading = false,
  reportDisabled = false,
  showPushTo3DX = false,
  pushTo3DXDisabled = true,
  dtrId,
}) => {
  const { stepper } = useStepperContext();
  const navigate = useNavigate();
  const { projectId } = useSelector((state: RootState) => state.define);
  const pathname = useLocation();
  const palletTypes = useSelector((state: RootState) => state.pallets.palletTypes);
  const [showValidationDetails, setShowValidationDetails] = useState(false);
  const [activeTab, setActiveTab] = useState<'palletFields' | 'lpnFields'>('palletFields');
  const [pushTo3DXPayload, setPushTo3DXPayload] = useState<{ sections: string[]; imageIds: number[] }>({ sections: [], imageIds: [] });
  const { data: validationStatus, } = useValidatePalletConfigQuery(projectId, { refetchOnMountOrArgChange: true });
  const [pushPdfTo3Dx, { isLoading: isPushingPdf }] = usePushPdfTo3DxMutation();
  const downloadButtonRef = useRef<DownloadButtonHandle>(null);
  const isSynopsisStep = stepper.current.id === '10';

  const handlePushTo3DX = async () => {

    if (!projectId) {
      showErrorToast("No project selected to push to 3DX");
      return;
    }
  
    try {
      const response = await pushPdfTo3Dx({ projectId, ...pushTo3DXPayload }).unwrap();
      showSuccessToast(response?.message || "Report pushed to 3DX successfully");
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message;
      showErrorToast(message || "Failed to push the report to 3DX");
    }
  };

  const handleNext = async () => {
    if (hasUnsavedChanges) {
      await onSubmit();
    }
    if (!hasErrors) {
      stepper.next();
    }
  };

  const handleBack = async () => {
    if (hasUnsavedChanges) {
      await onSubmit();
    }
    if (parseInt(stepper.current.id) > 1) {
      stepper.previous();
    } else {
      navigate("/");
    }
  };


  const getFieldLabel = (section: string, fieldName: string) => {
    const sectionMap = FieldNames[section as keyof typeof FieldNames];
    if (sectionMap) {
      return sectionMap[fieldName as keyof typeof sectionMap] || fieldName;
    }
    return fieldName;
  };

  const formatSectionName = (name: string) => {
    return name.replace(/([A-Z])/g, ' $1').trim();
  };

  return (
    <div className={cn(
      "x-space gap-4 fixed w-full flex-center justify-end bottom-0 right-0 bg-white shadow-lg border-t border-grey/30 h-[60px]",
      layout && "fixed w-full"
    )}>
      {validationStatus && (
        <>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => {
                setActiveTab('palletFields');
                setShowValidationDetails(true);
              }}
              className="flex items-center z-[1000] gap-2 px-4 py-2 rounded-md hover:bg-gray-100"
            >
              <div className={`w-3 h-3 rounded-full ${validationStatus.completedFields === validationStatus.totalFields ? 'bg-states-success' : 'bg-red-500'}`} />
              <span className=" text-xs lg:text-base">
                Required Pallet Fields {validationStatus.completedFields}/{validationStatus.totalFields}
              </span>
            </button>

            
          </div>

          <Dialog open={showValidationDetails} onOpenChange={setShowValidationDetails}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <div className="flex justify-between items-center mb-4">
                  <DialogTitle className="text-xl">Validation Status</DialogTitle>
                  {/* <div className="flex gap-2">
                    <button
                      onClick={() => setActiveTab('palletFields')}
                      className={`px-4 py-2 rounded-md ${activeTab === 'palletFields' ? 'bg-blue-500 text-white' : 'bg-gray-100'}`}
                    >
                      LPN Fields
                    
                    </button>
                    <button
                      onClick={() => setActiveTab('lpnFields')}
                      className={`px-4 py-2 rounded-md ${activeTab === 'lpnFields' ? 'bg-blue-500 text-white' : 'bg-gray-100'}`}
                    >
                      Pallet Fields
                    </button>
                  </div> */}
                </div>
              </DialogHeader>

              {activeTab === 'palletFields' ? (
                <div className="py-4">
                  <div className=" text-gray-500 mb-4">
                    {validationStatus.completedFields} of {validationStatus.totalFields} required fields completed
                  </div>
                  {validationStatus?.validationResults?.map((result: {
                    palletId?: number;
                    palletName?: string;
                    isValid?: boolean;
                    variant?: string;
                    errors?: Record<string, string[]>;

                  }, index: number) => (
                    <div key={result.palletId} className="mb-6 border border-gray-200 rounded-lg p-4">
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        <span className={result.isValid ? "text-green-500" : "text-red-500"}>●</span>
                        {result?.palletName} - {result?.variant}
                      </h3>

                      {result?.errors && Object.entries(result.errors).map(([section, fields]) => {
                        if (!fields || !Array.isArray(fields)) return null;
                        return (
                          <div key={section} className="mb-4">
                            <h4 className="font-medium mb-2">
                              {formatSectionName(section) || 'Unknown Section'}
                            </h4>
                            <ul className="list-disc pl-6">
                              {fields.map((field, fieldIndex) => (
                                <li key={`${field}`} className="text-red-600">
                                  {getFieldLabel(section, field) || field} is required
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                      {result?.isValid && (
                        <p className="text-green-600">All required fields are completed.</p>
                      )}
                    </div>
                  )) || (
                      <p className="text-gray-500">No validation results available</p>
                    )}
                </div>
              ) : (
                <div className="py-4">
                  {validationStatus.validationResults?.map((result) => {
                    const palletName = palletTypes?.find(p => p.id === result.palletId)?.name || `Pallet ${result.palletId}`;
                    const palletVariant = palletTypes?.find(p => p.id === result.palletId)?.variant || '';

                    if (!result.lpnValidation?.totalLpns) return null;

                    return (
                      <div key={result?.palletId} className="mb-4 p-4 border border-gray-200 rounded-lg">
                        <h3 className="font-semibold mb-2 flex items-center gap-2">
                          <span className={result.lpnValidation.isValid ? "text-green-500" : "text-orange-500"}>●</span>
                          {palletVariant} - {palletName}
                        </h3>
                        <div className=" text-gray-600">
                          {result.lpnValidation.incompleteLpns > 0 ? (
                            <span className="text-orange-500">
                              {result.lpnValidation.incompleteLpns} incomplete LPN(s)
                            </span>
                          ) : (
                            <span className="text-green-500">
                              All LPNs complete
                            </span>
                          )}
                          <span className="text-gray-500 ml-2">
                            (Total: {result.lpnValidation.totalLpns})
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}

      {
        (isSynopsisStep && hasUnsavedChanges) && (
          <div>
            <span className='text-[red] text-sm'>* Please save the changes to Generate the report or Push to 3DX.</span> 
          </div>
        )
      }


      {stepper.current.id !== '1' && <Button
        onClick={handleBack}
        type="button"
        className={cn(
          "lg:w-[130px] rounded-lg hover:bg-[#EDF5FF]  bg-white border-2 border-blue text-blue semi-bold-16 text-xs lg:text-md font-semibold"
        )}
      >
        <span><MoveLeft /></span> Back
      </Button>}

      {hasUnsavedChanges && (
        <Button
          onClick={onSubmit}
          disabled={isLoading}
          className="lg:w-[130px] text-xs flex items-center gap-1  hover:bg-[#EDF5FF]  bg-white  rounded-lg border-2 border-blue text-blue font-semibold semi-bold-16 lg:text-md"
        >
          {isLoading && <span><Loader2 size={20} className="animate-spin" /></span>}
          Save
        </Button>
      )}

      {!noNext && (
        <Button
          disabled={isLoading}
          className="lg:w-[130px] button-style bg-blue hover:bg-[#00336C]  rounded-lg flex items-center gap-2 semi-bold-16 text-xs lg:text-md font-semibold"
          onClick={handleNext}
        >
          {isLoading && <span><Loader2 size={20} className="animate-spin" /></span>}
          Next <span><MoveRight /></span>
        </Button>
      )}

      {!pathname.pathname.includes('offline') && (
        <DownloadButton
          ref={downloadButtonRef}
          projectId={projectId}
          disabled={reportDisabled || (isSynopsisStep && hasUnsavedChanges) || isLoading}
          showPushTo3DX={showPushTo3DX}
          pushTo3DXDisabled={false}
          isPushingPdf={isPushingPdf}
          onPushTo3Dx={handlePushTo3DX}
          setPushTo3DXPayload={setPushTo3DXPayload}
        />
      )}

      

      {showPushTo3DX && (
        <Button
          type="button"
          disabled={!dtrId || pushTo3DXDisabled || isPushingPdf || (isSynopsisStep && hasUnsavedChanges) || isLoading}
          onClick={() => downloadButtonRef.current?.open()}
          className="bg-blue hover:bg-[#00336C] flex items-center gap-2"
        >
          {isPushingPdf ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
          {isPushingPdf ? "Pushing..." : "Push to 3DX"}
        </Button>
      )}
    </div>
  );
};

export default Footer;


























